import { FC, ReactNode, useEffect, useState } from 'react';
import { ReactComponent as ArrowIcon } from 'src/Themes/assets/arrow.svg';
import { TableColumnEnumType } from 'src/Components/DataGrid';
import { TextHint } from 'src/Components';
import { useDebounce } from 'src/Hooks';
import classNames from 'classnames';
import SelectBoxBody from 'src/UI/SelectBox/SelectBoxBody';
import st from './DefFilterCommon.module.scss';

interface ISelectProps {
	/** Флаг открытой модалки */
	isShowModal: boolean;
	/** Флаг тробера */
	isWait?: boolean;
	/** Флаг множественного значения */
	multiple: boolean;
	/** Опции списка */
	options: TableColumnEnumType[];
	/** Функция серверного поиска */
	onServerSideSearch?: (searchText: string) => void;
	/** Массив выбранных ИД опций */
	selectedIds: string[];
	/** Функция установки новой коллекции ИД выбранных опций */
	setSelected: (values: string[]) => void;
	/** Рендер кнопок отмена/применить */
	renderButtons: () => ReactNode;
}

const getTitle = (option: TableColumnEnumType): string => {
	return option.title ?? option.id;
};

/** Компонента отображающая выпадающий список */
const Select: FC<ISelectProps> = (props) => {
	const {
		isShowModal,
		isWait = false,
		multiple,
		options,
		selectedIds,
		setSelected,
		renderButtons,
		onServerSideSearch,
	} = props;

	const [search, setSearch] = useDebounce<string>(onSearchHandler, '');
	const [filteredOptions, setFilteredOptions] =
		useState<TableColumnEnumType[]>(options);

	useEffect(() => {
		setSearch('');
	}, [isShowModal]);

	useEffect(() => {
		if (onServerSideSearch) {
			setFilteredOptions(options);
		}
	}, [options, onServerSideSearch]);

	function onSearchHandler(searchText: string) {
		if (onServerSideSearch) {
			onServerSideSearch(searchText);
		} else {
			setFilteredOptions(
				options.filter((o) => {
					return (
						getTitle(o)
							.toLowerCase()
							.indexOf(searchText.toLowerCase()) !== -1
					);
				}),
			);
		}
	}

	const onChangeSelectedHandler = (value: TableColumnEnumType) => {
		if (multiple) {
			const updatedList = selectedIds.includes(value.id)
				? selectedIds.filter((id) => {
						return id !== value.id;
					})
				: [...selectedIds, value.id];
			setSelected(updatedList);
		} else {
			setSelected([value.id]);
		}
	};

	const getLabel = (): string => {
		const list: string[] = [];
		selectedIds.forEach((id) => {
			const opt = options.find((o) => {
				return o.id === id;
			});
			if (opt) {
				list.push(getTitle(opt));
			}
		});
		return list.join(', ');
	};

	const renderInput = () => {
		if (isShowModal) {
			return (
				<input
					value={search}
					onChange={(e) => {
						return setSearch(e.target.value);
					}}
					placeholder="Поиск по всему списку"
				/>
			);
		} else {
			return (
				<TextHint
					className={st.text}
					isList={multiple}
					disable={isShowModal}
				>
					{getLabel()}
				</TextHint>
			);
		}
	};

	return (
		<>
			<ArrowIcon />
			{renderInput()}
			{isWait && <div className={classNames('rdc_spiner', st.trober)} />}
			{isShowModal && (
				<div
					className={classNames(
						'body-small',
						st.modalBody,
						st.styles,
					)}
				>
					<SelectBoxBody
						isWait={isWait}
						valueField="id"
						titleField="title"
						searchText={search}
						minSearchLength={1}
						options={filteredOptions}
						selectedIds={selectedIds}
						multiple={multiple}
						onClickOption={onChangeSelectedHandler}
						renderListTb={renderButtons}
					/>
				</div>
			)}
		</>
	);
};

export default Select;
